import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiX,
} from "react-icons/fi";

const validTime = (value) =>
  /^([01]\d|2[0-3]):[0-5]\d$/.test(value || "");

export default function CreateLoadTimeSchedulePicker({
  kind,
  initialType = "FIXED",
  initialStart = "",
  initialEnd = "",
  position,
  onApply,
  onClose,
}) {
  const [mode, setMode] = useState(
    initialType === "WINDOW" ? "WINDOW" : "FIXED"
  );

  const [start, setStart] = useState(
    validTime(initialStart) ? initialStart : "09:00"
  );

  const [end, setEnd] = useState(
    validTime(initialEnd) ? initialEnd : "16:00"
  );

  const [error, setError] = useState("");

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  const apply = () => {
    if (!validTime(start)) {
      setError("Choose a valid appointment or start time.");
      return;
    }

    if (
      mode === "WINDOW" &&
      (!validTime(end) || end <= start)
    ) {
      setError(
        "The end time must be later than the start time on the same day."
      );
      return;
    }

    onApply({
      scheduleType: mode,
      hour: start,
      hourEnd: mode === "WINDOW" ? end : null,
    });
  };

  return createPortal(
    <div
      className="loadevo-schedule-backdrop"
      onMouseDown={onClose}
    >
      <div
        className="loadevo-schedule-modal"
        style={{
          top: position.top,
          left: position.left,
        }}
        role="dialog"
        aria-modal="true"
        aria-label={
          `${kind === "pickup" ? "Pickup" : "Delivery"} schedule`
        }
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="loadevo-schedule-header">
          <div className="loadevo-schedule-heading">
            <FiCalendar aria-hidden="true" />

            <strong>
              {kind === "pickup"
                ? "Pickup schedule"
                : "Delivery schedule"}
            </strong>
          </div>

          <button
            type="button"
            className="loadevo-schedule-close"
            onClick={onClose}
            aria-label="Close schedule"
          >
            <FiX aria-hidden="true" />
          </button>
        </div>

        <div
          className="loadevo-schedule-options"
          role="group"
          aria-label="Time selection mode"
        >
          <button
            type="button"
            className={`loadevo-schedule-option${
              mode === "FIXED" ? " is-active" : ""
            }`}
            aria-pressed={mode === "FIXED"}
            onClick={() => {
              setMode("FIXED");
              setError("");
            }}
          >
            <span className="loadevo-schedule-option-top">
              <FiClock aria-hidden="true" />

              {mode === "FIXED" && (
                <FiCheckCircle aria-hidden="true" />
              )}
            </span>

            <strong>Fixed time</strong>
            <small>Specific appointment</small>
          </button>

          <button
            type="button"
            className={`loadevo-schedule-option${
              mode === "WINDOW" ? " is-active" : ""
            }`}
            aria-pressed={mode === "WINDOW"}
            onClick={() => {
              setMode("WINDOW");
              setError("");
            }}
          >
            <span className="loadevo-schedule-option-top">
              <FiCalendar aria-hidden="true" />

              {mode === "WINDOW" && (
                <FiCheckCircle aria-hidden="true" />
              )}
            </span>

            <strong>Time window</strong>
            <small>Flexible arrival</small>
          </button>
        </div>

        <div
          className={`loadevo-schedule-fields${
            mode === "WINDOW" ? " is-window" : ""
          }`}
        >
          <label>
            {mode === "WINDOW" ? "From" : "Appointment time"}

            <input
              type="time"
              value={start}
              onChange={(event) => {
                setStart(event.target.value);
                setError("");
              }}
            />
          </label>

          {mode === "WINDOW" && (
            <label>
              Until

              <input
                type="time"
                value={end}
                onChange={(event) => {
                  setEnd(event.target.value);
                  setError("");
                }}
              />
            </label>
          )}
        </div>

        {error && (
          <p
            className="loadevo-schedule-error"
            role="alert"
          >
            {error}
          </p>
        )}

        <button
          type="button"
          className="loadevo-schedule-apply"
          onClick={apply}
        >
          Apply schedule
        </button>
      </div>
    </div>,
    document.body
  );
}